/**
 * sc-pickup-location
 *
 * Populates inventory-location text into any element marked
 * `[data-pickup-location-variant-id="<variant_id>"]` by fetching the same
 * data path PDPs use:
 *   GET /variants/{variant_id}/?section_id=pickup-availability
 *
 * Why fetch? Because `item.variant.store_availabilities` is empty in the
 * cart Liquid render context — Shopify only populates it inside the
 * pickup-availability section render. The diagnostic that confirmed this
 * lives in .planning/cart-stock-line-diag.md (Agent 2).
 *
 * Cache: per-variant_id, value is the in-flight Promise. Concurrent calls
 * for the same variant share the same request. Result is the location
 * name string (or null if there is no pickup-enabled location).
 *
 * Re-entry: idempotent. Re-running with already-populated placeholders is
 * a no-op (we skip elements that already have non-empty textContent).
 */
(function () {
  if (window.__scPickupLocationWired) return;
  window.__scPickupLocationWired = true;

  const ICON_TICK_SVG = `<svg class="icon icon-tick" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20"><path fill="#108043" stroke="#fff" d="M16.136 6.979h0l.003-.004a1.499 1.499 0 00-2.275-1.954l-5.945 6.777-1.858-1.859A1.499 1.499 0 103.94 12.06l2.999 3s0 0 0 0c.289.29.675.44 1.1.439h.019a1.5 1.5 0 001.08-.522l6.998-7.998z"/></svg>`;

  const cache = new Map(); // variantId(string) -> Promise<string|null>

  function fetchLocationName(variantId) {
    if (cache.has(variantId)) return cache.get(variantId);
    const rootUrl = (window.routes && window.routes.root_url) || '';
    const url = `${rootUrl}/variants/${encodeURIComponent(variantId)}/?section_id=pickup-availability`;
    const p = fetch(url, { headers: { Accept: 'text/html' } })
      .then((r) => (r.ok ? r.text() : ''))
      .then((html) => {
        if (!html) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // The drawer markup in sections/pickup-availability.liquid renders one
        // <h3 class="pickup-availability-list-Heading"> per pickup-enabled
        // location. First one is the closest/primary location.
        const heading = doc.querySelector('.pickup-availability-list-Heading');
        if (heading && heading.textContent.trim()) return heading.textContent.trim();
        // Fallback: the preview <p class="caption-large"> contains
        // "Pickup available at <strong>Location Name</strong>" via the
        // translation pick_up_available_at_html. Extract the <strong>.
        const strong = doc.querySelector('.pickup-availability-preview .caption-large strong');
        if (strong && strong.textContent.trim()) return strong.textContent.trim();
        return null;
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[sc-pickup-location] fetch failed for variant', variantId, err);
        return null;
      });
    cache.set(variantId, p);
    return p;
  }

  // Map raw Shopify location names to customer-facing display names.
  // Keep keys as the EXACT Shopify location name; values are what the
  // customer sees. Locations not in the map render their raw name.
  const LOCATION_RENAME = {
    'DuPont': 'DC store',
    'DuPont store': 'DC store',
    'Dupont': 'DC store',
    'Dupont store': 'DC store',
    // Charlotte stays as-is — shows whatever Shopify returns
  };

  function displayLocation(rawName) {
    return LOCATION_RENAME[rawName] || rawName;
  }

  function paint(el, locationName) {
    if (!el || !el.isConnected) return;
    if (!locationName) {
      el.setAttribute('hidden', '');
      el.textContent = '';
      return;
    }
    const display = displayLocation(locationName);
    el.innerHTML = `${ICON_TICK_SVG}<span>1 in stock at ${escapeHtml(display)}</span>`;
    el.removeAttribute('hidden');
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function populate(root) {
    const scope = root || document;
    const placeholders = scope.querySelectorAll('[data-pickup-location-variant-id]');
    placeholders.forEach((el) => {
      // Skip elements we've already painted (textContent is the location name)
      // unless the placeholder was reset by a parent re-render — in which case
      // textContent is empty so we still fall through.
      if (el.textContent && el.textContent.trim()) return;
      const variantId = el.getAttribute('data-pickup-location-variant-id');
      if (!variantId) return;
      fetchLocationName(variantId).then((name) => paint(el, name));
    });
  }

  // Public entry point — callable from drawer connectedCallback() / open(),
  // from cart-page bootstrap, or from any cartUpdate subscriber.
  window.scPopulatePickupLocations = populate;

  function init() {
    populate();
    if (typeof window.subscribe === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        // After a cart-level change, the drawer may have new line items
        // rendered server-side via the sections= round-trip. Re-scan.
        // Existing placeholders with text stay painted; new ones get fetched.
        populate();
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
